import { updateSession } from '@/lib/auth-session'
import api, { ApiError, type BlobRequestOptions } from '@/lib/base-api'
import { toAgentFormData, toRoomFormData } from '@/lib/form'
import { filterEnabledModels } from '@/lib/ai-models'
import { useAiModelStore } from '@/store/useAiModelStore'
import { useAuthStore } from '@/store/useAuthStore'
import { useBundleChoiceStore } from '@/store/useBundleChoiceStore'
import { useChatStore } from '@/store/useChatStore'
import type { DataFile } from '@/types'
import type {
  AgentAutofillResultsRequest,
  AgentCreateRequest,
  AgentUpdateRequest,
  BillingExportParams,
  BillingUsageParams,
  BroadcastCampaignCreateRequest,
  BroadcastCampaignUpdateRequest,
  BroadcastMessageTemplateCreateRequest,
  BroadcastMessageTemplateUpdateRequest,
  BroadcastCampaignListParams,
  BroadcastSegmentCreateRequest,
  BroadcastSegmentPreviewRequest,
  BroadcastSegmentUpdateRequest,
  ChangePasswordRequest,
  ChatFeatureUpdateRequest,
  ChatMessageCreateRequest,
  ChatSessionCreateRequest,
  ChatSessionUpdateRequest,
  CompanyModelSettingsUpdateRequest,
  CompanySettingsUpdateRequest,
  DashboardParams,
  DataManagementFileImportRequest,
  DataManagementFileUpdateRequest,
  DataManagementFolderRequest,
  DataManagementFolderUpdateRequest,
  DataManagementItemEditRequest,
  DataManagementItemsDeleteRequest,
  DataManagementItemsDownloadRequest,
  DepartmentCreateRequest,
  DepartmentUpdateRequest,
  DynamicTagCategoryCreateRequest,
  DynamicTagCategoryUpdateRequest,
  EditConversationRequest,
  ForgotPasswordRequest,
  LineIntegrationCreateRequest,
  LineIntegrationUpdateRequest,
  MessengerIntegrationCreateRequest,
  MessengerIntegrationUpdateRequest,
  MFARecoveryRequest,
  PaginationParams,
  RoomCreateRequest,
  RoomUpdateRequest,
  SessionCategoryCreateRequest,
  SessionCategoryUpdateRequest,
  SetPasswordWithTokenRequest,
  ShareRoomRequest,
  ShareSessionRequest,
  SSOLoginEntraIDRequest,
  SSOLoginGoogleRequest,
  StaticTagRuleCreateRequest,
  StaticTagRuleUpdateRequest,
  StaticTagCreateRequest,
  StaticTagMergeRequest,
  StaticTagUpdateRequest,
  TOTPTokenRequest,
  UserCreateRequest,
  UserUpdateRequest,
} from '@/types/request'
import type {
  AccountAPIKeyResponse,
  AIModelProviderResponse,
  Agent,
  AuditLogResponse,
  BillingUsageResponse,
  BroadcastCampaignEnableResponse,
  BroadcastMessageTemplateResponse,
  BroadcastCampaignDetailResponse,
  BroadcastCampaignResponse,
  BroadcastResultResponse,
  BroadcastMessageTemplateTestBroadcastResponse,
  BroadcastSegmentDetailResponse,
  BroadcastSegmentImpactResponse,
  BroadcastSegmentPreviewResponse,
  BroadcastSegmentResponse,
  ChatFeatureResponse,
  ChangePasswordResponse,
  CompanyAgentResponse,
  CompanySettingsResponse,
  ConversationResponse,
  DataManagementBulkActionResponse,
  DataManagementFileImportResponse,
  DataManagementFilePreviewResponse,
  DataManagementFileUpdateResponse,
  DataManagementFolderCreateResponse,
  DataManagementFolderUpdateResponse,
  DashboardResponse,
  Department,
  DynamicTagCategoryResponse,
  DynamicTagResponse,
  FileUploadResponse,
  LineIntegrationResponse,
  LoginResponse,
  MessageResponse,
  MessengerIntegrationResponse,
  MFAResponse,
  MFAResetResponse,
  MFASetupResponse,
  MFAValidationResponse,
  PaginatedResponse,
  RoomResponse,
  SessionDetailResponse,
  SessionFolderResponse,
  SessionFolderDetailResponse,
  AgentAutofillResult,
  SessionCategoryResponse,
  SessionResponse,
  ShareRoomDetailResponse,
  StaticTagResponse,
  TagImpactResponse,
  StaticTagRuleResponse,
  StaticTagGroupResponse,
  UserResponse,
  ValidateTokenResponse,
  VersionItemResponse,
  LineUserProfileResponse,
  WhoAmIResponse,
} from '@/types/response'
import type {
  Signal,
  SignalCreateRequest,
  SignalListItem,
  SignalScraperType,
  SignalUpdateRequest,
} from '@/types/signal'
import { usePermissions } from '@/hooks/usePermissions'
import { Permissions } from '@/lib/permissions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// ============================================
// Common Hooks
// ============================================
export function useFileUpload() {
  return useMutation({
    mutationFn: ({
      file,
      targetType,
      onProgress,
    }: {
      file: File
      targetType: 'broadcast_message_template_image'
      onProgress?: (progress: number) => void
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('target_type', targetType)
      return api.post<FileUploadResponse>('/api/common/files', formData, {
        adapter: 'xhr',
        onUploadProgress: onProgress,
      })
    },
  })
}

// ============================================
// Auth Hooks
// ============================================
export function useWhoAmI(enabled = true) {
  return useQuery({
    queryKey: ['whoami'],
    queryFn: ({ signal }) => api.get<WhoAmIResponse>('/api/account/whoami', { signal }),
    retry: false,
    staleTime: 0,
    refetchInterval: 60_000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchOnMount: 'always',
    enabled,
  })
}

export function useLogin() {
  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      api.post<LoginResponse>('/api/account/login', {
        username,
        password,
      }),
  })
}

export function useGoogleSSOLogin() {
  return useMutation({
    mutationFn: (data: SSOLoginGoogleRequest) => api.post<LoginResponse>('/api/account/sso-login/google', data),
  })
}

export function useEntraIDSSOLogin() {
  return useMutation({
    mutationFn: (data: SSOLoginEntraIDRequest) => api.post<LoginResponse>('/api/account/sso-login/entra-id', data),
  })
}

export function useForgotPasswordRequestLink() {
  return useMutation({
    mutationFn: (data: ForgotPasswordRequest) =>
      api.post<MessageResponse>('/api/account/forgot-password/request-link', data),
  })
}

export function useValidateSetPasswordToken(token: string | null) {
  return useQuery({
    queryKey: ['set-password-token', token],
    queryFn: () =>
      api.get<ValidateTokenResponse>('/api/account/set-password/validate-token', {
        params: { token },
      }),
    enabled: !!token,
    retry: false,
  })
}

export function useSetPasswordWithToken() {
  return useMutation({
    mutationFn: (data: SetPasswordWithTokenRequest) =>
      api.post<MessageResponse>('/api/account/set-password-with-token', data),
  })
}

export function useChangePassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) =>
      updateSession(async () => {
        const response = await api.post<ChangePasswordResponse>('/api/account/change-password', data)
        useAuthStore.getState().setAuthTokens(response.access_token, response.refresh_token)
        useAuthStore.getState().setPasswordExpired(response.is_password_expired)
        return response
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['whoami'] })
      void qc.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'whoami' })
    },
  })
}

export function useMFASetup() {
  return useMutation({
    mutationFn: () => api.post<MFASetupResponse>('/api/account/mfa-setup'),
  })
}

export function useMFADeleteByAdmin() {
  return useMutation({
    mutationFn: (id: number) => api.post<MFAResetResponse>(`/api/account/mfa-reset/${id}`),
  })
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: (id: number) => api.post<MessageResponse>(`/api/account/users/${id}/reset-password`),
  })
}

export function useMFAValidation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: TOTPTokenRequest) => api.post<MFAValidationResponse>('/api/account/mfa-verify', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['whoami'] })
    },
  })
}

export function useMFARecovery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: MFARecoveryRequest) => api.post<MFAResponse>('/api/account/mfa-recovery', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['whoami'] })
    },
  })
}

export function useAccountAPIKey() {
  return useQuery({
    queryKey: ['account-api-key'],
    queryFn: async () => {
      try {
        return await api.get<AccountAPIKeyResponse>('/api/account/api-key')
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null
        throw error
      }
    },
    retry: false,
  })
}

export function useRotateAccountAPIKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<AccountAPIKeyResponse>('/api/account/api-key', {}),
    onSuccess: (data) => qc.setQueryData(['account-api-key'], data),
  })
}

// ============================================
// Chat Hooks
// ============================================
export function useSessions(
  params?: PaginationParams & {
    view?: 'user' | 'admin' | 'shared'
    status?: string
    category?: string
    sentiment?: string
  },
) {
  return useQuery({
    queryKey: ['sessions', 'list', params],
    queryFn: () =>
      api.get<PaginatedResponse<SessionResponse>>('/api/chat/sessions', {
        params,
      }),
    enabled: !!params,
  })
}

export function useSessionFolders() {
  return useQuery({
    queryKey: ['session-folders', 'list'],
    queryFn: ({ signal }) => api.get<SessionFolderResponse[]>('/api/chat/session-folders', { signal }),
  })
}

export function useSessionFolder(id: number, enabled: boolean) {
  return useQuery({
    queryKey: ['session-folders', 'detail', id],
    queryFn: ({ signal }) => api.get<SessionFolderDetailResponse>(`/api/chat/session-folders/${id}`, { signal }),
    enabled,
  })
}

export function useCreateSessionFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string }) => api.post<SessionFolderResponse>('/api/chat/session-folders', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session-folders', 'list'] }),
  })
}

export function useRenameSessionFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api.patch<SessionFolderResponse>(`/api/chat/session-folders/${id}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session-folders'] }),
  })
}

export function useDeleteSessionFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<{ detail: string }>(`/api/chat/session-folders/${id}`),
    onSuccess: (_, id) => {
      const { session } = useChatStore.getState()
      if (session?.folder_id === id) {
        useChatStore.setState({ session: { ...session, folder_id: null } })
      }
      qc.removeQueries({ queryKey: ['session-folders', 'detail', id] })
      return Promise.all([
        qc.invalidateQueries({ queryKey: ['session-folders', 'list'] }),
        qc.invalidateQueries({ queryKey: ['sessions'] }),
      ])
    },
  })
}

export function useSessionDetail(sessionId?: number) {
  return useQuery({
    queryKey: ['sessions', 'detail', sessionId],
    queryFn: () => api.get<SessionDetailResponse>(`/api/chat/session/${sessionId}`),
    enabled: typeof sessionId === 'number' && Number.isFinite(sessionId),
  })
}

export function useFetchSessionBranch() {
  const qc = useQueryClient()
  return (sessionId: number, branchId: string | null) =>
    qc.fetchQuery({
      queryKey: ['sessions', sessionId, branchId],
      queryFn: () =>
        api.get<SessionDetailResponse>(`/api/chat/session/${sessionId}`, {
          params: branchId ? { branch_id: branchId } : undefined,
        }),
    })
}

export function useMessageVersions(conversationId?: number) {
  return useQuery({
    queryKey: ['message-versions', conversationId],
    queryFn: () => api.get<VersionItemResponse[]>(`/api/chat/message/${conversationId}/versions`),
    enabled: typeof conversationId === 'number',
  })
}

export function useCreateSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data?: ChatSessionCreateRequest) => api.post<SessionDetailResponse>('/api/chat/sessions', data ?? {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions', 'list'] }),
  })
}

export function useUpdateShareSession(sessionId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ShareSessionRequest) => api.post<MessageResponse>(`/api/chat/session/${sessionId}/share`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions'] })
    },
  })
}

export function useUpdateSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ChatSessionUpdateRequest }) =>
      api.patch<SessionResponse>(`/api/chat/session/${id}`, data),
    onSuccess: (session) => {
      qc.invalidateQueries({ queryKey: ['sessions', 'list'] })
      qc.invalidateQueries({ queryKey: ['session-folders'] })
      qc.invalidateQueries({ queryKey: ['sessions', 'detail', session.id] })
      qc.invalidateQueries({ queryKey: ['sessions', session.id] })
      useChatStore.getState().renameSession(session)
    },
  })
}

export function useDeleteSession({ onBeforeInvalidate }: { onBeforeInvalidate?: (id: number) => void } = {}) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/chat/session/${id}`),
    onSuccess: (_, id) => {
      if (useChatStore.getState().session?.id === id) {
        useChatStore.getState().createTempSession()
      }
      onBeforeInvalidate?.(id)
      qc.invalidateQueries({ queryKey: ['sessions', 'list'] })
      qc.invalidateQueries({ queryKey: ['session-folders'] })
    },
  })
}

export function useSendMessage() {
  return useMutation({
    mutationFn: (data: ChatMessageCreateRequest) => {
      const formData = new FormData()
      formData.append('session_id', String(data.session_id))
      formData.append('content', data.content)
      if (data.model_version_id != null) formData.append('model_version_id', String(data.model_version_id))
      if (data.agent_id != null) formData.append('agent_id', String(data.agent_id))
      if (data.effort_level != null) formData.append('effort_level', data.effort_level)
      data.upload_documents?.forEach((doc) => formData.append('upload_documents', doc.file, doc.name))
      return api.postStream('/api/chat/messages', formData, { headers: { Accept: 'text/event-stream' } })
    },
  })
}

export function useRetryMessage() {
  return useMutation({
    mutationFn: ({ pk }: { pk: number }) =>
      api.postStream(`/api/chat/message/${pk}/retry`, undefined, { headers: { Accept: 'text/event-stream' } }),
  })
}

export function useEditMessage() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ pk, data }: { pk: number; data: EditConversationRequest }) =>
      api.postStream(`/api/chat/message/${pk}/edit`, data, { headers: { Accept: 'text/event-stream' } }),
  })
  return { ...mutation, invalidate: () => qc.invalidateQueries({ queryKey: ['sessions'] }) }
}

export function useRegenerateMessage() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      pk,
      model_version_id,
      agent_id,
    }: {
      pk: number
      model_version_id?: number | null
      agent_id?: number | null
    }) =>
      api.postStream(
        `/api/chat/message/${pk}/regenerate`,
        { model_version_id, agent_id },
        { headers: { Accept: 'text/event-stream' } },
      ),
  })
  return { ...mutation, invalidate: () => qc.invalidateQueries({ queryKey: ['message-versions'] }) }
}

export function useConversationFeedback() {
  return useMutation({
    mutationFn: ({ pk, feedback }: { pk: number; feedback: boolean | null }) =>
      api.patch<ConversationResponse>(`/api/chat/message/${pk}/feedback`, { feedback }),
  })
}

export function useChatFeature() {
  return useQuery({
    queryKey: ['chat-feature'],
    queryFn: () => api.get<ChatFeatureResponse>('/api/company/chat-feature'),
  })
}

export function useCompanyAgents(enabled = true) {
  return useQuery({
    queryKey: ['company-agents'],
    queryFn: () => api.get<CompanyAgentResponse[]>('/api/company/agents'),
    enabled,
  })
}

export function useUpdateChatFeature() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ChatFeatureUpdateRequest) => api.patch<ChatFeatureResponse>('/api/company/chat-feature', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-feature'] }),
  })
}

export function useCompanySettings() {
  return useQuery({
    queryKey: ['company-settings'],
    queryFn: () => api.get<CompanySettingsResponse>('/api/company/settings'),
  })
}

export function useUpdateCompanySettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CompanySettingsUpdateRequest) =>
      api.patch<CompanySettingsResponse>('/api/company/settings', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company-settings'] }),
  })
}

export function useUpdateCompanyModelSettings() {
  const qc = useQueryClient()
  const fetchModels = useAiModelStore((state) => state.fetchModels)
  return useMutation({
    mutationFn: (data: CompanyModelSettingsUpdateRequest) => api.post<MessageResponse>('/api/company/models', data),
    onSuccess: () => Promise.all([fetchModels(), qc.invalidateQueries({ queryKey: ['company-models'] })]),
  })
}

export function useDepartments(params?: PaginationParams) {
  return useQuery({
    queryKey: ['departments', params],
    queryFn: () =>
      api.get<PaginatedResponse<Department>>('/api/company/departments', {
        params,
      }),
    enabled: !!params,
  })
}

export function useDepartment(id: number | undefined) {
  return useQuery({
    queryKey: ['department', id],
    queryFn: () => api.get<Department>(`/api/company/department/${id}`),
    enabled: !!id,
  })
}

export function useCreateDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: DepartmentCreateRequest) => api.post<Department>('/api/company/departments', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
  })
}

export function useDeleteDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<MessageResponse>(`/api/company/department/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
  })
}

export function useUpdateDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DepartmentUpdateRequest }) =>
      api.patch<Department>(`/api/company/department/${id}`, data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['departments'] })
      qc.setQueryData(['department', data.id], data)
    },
  })
}

export function useDashboard(params?: DashboardParams) {
  return useQuery({
    queryKey: ['dashboard', params],
    queryFn: () => api.get<DashboardResponse>('/api/dashboard', { params }),
    staleTime: 60 * 1000,
    enabled: params !== undefined,
  })
}

export function useBillingUsage(params?: BillingUsageParams) {
  return useQuery({
    queryKey: ['billing-usage', params],
    queryFn: () => api.get<BillingUsageResponse>('/api/billing/usage', { params }),
    enabled: params !== undefined,
  })
}

export function useBillingExport() {
  return useMutation({
    mutationFn: (params: BillingExportParams) => api.getBlob('/api/billing/export', { params }),
  })
}

// ============================================
// Data Management Hooks
// ============================================
const SIGNALS_URL = '/api/data-management/signals'
const SIGNAL_QUERY_KEY = ['data-management', 'signals']

export function useSignals(params?: PaginationParams & { scraper_type?: SignalScraperType }) {
  const { has } = usePermissions()
  return useQuery({
    queryKey: [...SIGNAL_QUERY_KEY, params],
    queryFn: () => api.get<PaginatedResponse<SignalListItem>>(SIGNALS_URL, { params }),
    enabled: has(Permissions.data_management__view_signal),
    retry: false,
  })
}

export function useSignal(id?: number) {
  const { has } = usePermissions()
  return useQuery({
    queryKey: [...SIGNAL_QUERY_KEY, id],
    queryFn: () => api.get<Signal>(`${SIGNALS_URL}/${id}`),
    enabled: !!id && has(Permissions.data_management__view_signal),
    retry: false,
  })
}

export function useCreateSignal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: SignalCreateRequest) => api.post<Signal>(SIGNALS_URL, data),
    retry: false,
    onSettled: () => qc.invalidateQueries({ queryKey: SIGNAL_QUERY_KEY }),
  })
}

export function useUpdateSignal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SignalUpdateRequest }) =>
      api.patch<Signal>(`${SIGNALS_URL}/${id}`, data),
    retry: false,
    onSettled: () => qc.invalidateQueries({ queryKey: SIGNAL_QUERY_KEY }),
  })
}

export function useDeleteSignal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`${SIGNALS_URL}/${id}`),
    retry: false,
    onSettled: () => qc.invalidateQueries({ queryKey: SIGNAL_QUERY_KEY }),
  })
}

export function useDatamanagementItems(
  params?: PaginationParams & {
    session?: number | null
    folder?: number | null
    department?: string | null
    priority?: string | null
    status?: DataFile['status']
  },
) {
  return useQuery({
    queryKey: ['data-management', 'items', params],
    queryFn: () => api.get<PaginatedResponse<DataFile>>('/api/data-management/items', { params }),
    enabled: !!params,
  })
}

export function useCreateDataManagementFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: DataManagementFolderRequest) =>
      api.post<DataManagementFolderCreateResponse>('/api/data-management/folders', data),
    onSuccess: () => {
      useBundleChoiceStore.getState().refresh()
      return qc.invalidateQueries({ queryKey: ['data-management', 'items'] })
    },
  })
}

export function useUpdateDataManagementFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DataManagementFolderUpdateRequest }) =>
      api.patch<DataManagementFolderUpdateResponse>(`/api/data-management/folders/${id}`, data),
    onSuccess: () => {
      useBundleChoiceStore.getState().refresh()
      return qc.invalidateQueries({ queryKey: ['data-management', 'items'] })
    },
  })
}

export function useImportDataManagementFiles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ files, folderId }: DataManagementFileImportRequest) => {
      const formData = new FormData()
      files.forEach((file) => formData.append('files', file))
      if (folderId) formData.append('folder_id', String(folderId))
      return api.post<DataManagementFileImportResponse>('/api/data-management/files/import', formData)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-management', 'items'] }),
  })
}

export function useDeleteItems() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fileIds = [], folderIds = [], session }: DataManagementItemsDeleteRequest) => {
      const params = new URLSearchParams()
      fileIds.forEach((id) => params.append('file_ids', String(id)))
      folderIds.forEach((id) => params.append('folder_ids', String(id)))
      if (session) params.set('session', String(session))
      return api.delete<DataManagementBulkActionResponse>(
        `/api/data-management/files/bulk-actions?${params.toString()}`,
      )
    },
    onSuccess: (_data, { folderIds }) => {
      if (folderIds?.length) useBundleChoiceStore.getState().refresh()
      return qc.invalidateQueries({ queryKey: ['data-management', 'items'] })
    },
  })
}

export function useEditItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      fileIds = [],
      folderIds = [],
      departmentIds,
      startLifeDate,
      expiredLifeDate,
      folderId,
      priority,
    }: DataManagementItemEditRequest) =>
      api.patch<DataManagementBulkActionResponse>('/api/data-management/files/bulk-actions', {
        file_ids: fileIds,
        folder_ids: folderIds,
        ...(departmentIds !== undefined && { department_ids: departmentIds }),
        ...(startLifeDate !== undefined && { start_life_date: startLifeDate }),
        ...(expiredLifeDate !== undefined && { expired_life_date: expiredLifeDate }),
        ...(folderId !== undefined && { folder_id: folderId }),
        ...(priority !== undefined && { priority }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-management', 'items'] }),
  })
}

export function useUpdateDatamanagementFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fileId, departmentIds, startLifeDate, expiredLifeDate }: DataManagementFileUpdateRequest) =>
      api.patch<DataManagementFileUpdateResponse>(`/api/data-management/files/${fileId}`, {
        ...(departmentIds !== undefined && { department_ids: departmentIds }),
        ...(startLifeDate !== undefined && { start_life_date: startLifeDate }),
        ...(expiredLifeDate !== undefined && { expired_life_date: expiredLifeDate }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-management', 'items'] }),
  })
}

export function useDataManagementFilePreview(fileId: number | undefined, open: boolean) {
  return useQuery({
    queryKey: ['data-management', 'file-preview', fileId],
    queryFn: () => api.get<DataManagementFilePreviewResponse>(`/api/data-management/files/${fileId}/preview`),
    enabled: open && fileId !== undefined,
  })
}

export function useDownloadFile() {
  return useMutation({
    mutationFn: ({ fileId, ...options }: { fileId: number } & BlobRequestOptions) =>
      api.getBlob(`/api/data-management/files/${fileId}/download`, options),
  })
}

export function useDownloadItems() {
  return useMutation({
    mutationFn: ({ fileIds = [], folderIds = [] }: DataManagementItemsDownloadRequest) =>
      api.postBlob('/api/data-management/files/bulk-download', {
        file_ids: fileIds,
        folder_ids: folderIds,
      }),
  })
}

// ============================================
// User Management Hooks
// ============================================
export function useUsers(params?: PaginationParams & { is_active?: boolean }) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () =>
      api.get<PaginatedResponse<UserResponse>>('/api/account/users', {
        params,
      }),
    enabled: !!params,
  })
}

export function useUser(id: number | undefined) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => api.get<UserResponse>(`/api/account/users/${id}`),
    enabled: !!id,
  })
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UserCreateRequest) => api.post<UserResponse>('/api/account/users', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UserUpdateRequest }) =>
      api.patch<UserResponse>(`/api/account/users/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/account/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useSetUserActive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api.post<Pick<UserResponse, 'id' | 'is_active'>>(
        `/api/account/users/${id}/${isActive ? 'activate' : 'deactivate'}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

// ============================================
// Integration Hooks
// ============================================
export function useLineIntegrations(ordering?: string) {
  return useQuery({
    queryKey: ['integrations', 'line', { ordering }],
    queryFn: () => api.get<LineIntegrationResponse[]>('/api/integration/line', { params: { ordering } }),
  })
}

export function useLineIntegration(id: number | undefined) {
  return useQuery({
    queryKey: ['integrations', 'line', id],
    queryFn: () => api.get<LineIntegrationResponse>(`/api/integration/line/${id}`),
    enabled: !!id,
  })
}

export function useMessengerIntegrations(ordering?: string) {
  return useQuery({
    queryKey: ['integrations', 'messenger', { ordering }],
    queryFn: () => api.get<MessengerIntegrationResponse[]>('/api/integration/messenger', { params: { ordering } }),
  })
}

export function useMessengerIntegration(id: number | undefined) {
  return useQuery({
    queryKey: ['integrations', 'messenger', id],
    queryFn: () => api.get<MessengerIntegrationResponse>(`/api/integration/messenger/${id}`),
    enabled: !!id,
  })
}

export function useCreateLineIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: LineIntegrationCreateRequest) =>
      api.post<LineIntegrationResponse>('/api/integration/line', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  })
}

export function useUpdateLineIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LineIntegrationUpdateRequest }) =>
      api.patch<LineIntegrationResponse>(`/api/integration/line/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  })
}

export function useDeleteLineIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/integration/line/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  })
}

export function useCreateMessengerIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: MessengerIntegrationCreateRequest) =>
      api.post<MessengerIntegrationResponse>('/api/integration/messenger', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  })
}

export function useUpdateMessengerIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MessengerIntegrationUpdateRequest }) =>
      api.patch<MessengerIntegrationResponse>(`/api/integration/messenger/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  })
}

export function useDeleteMessengerIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/integration/messenger/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  })
}

export function useLineUserProfiles(
  params?: PaginationParams & { static_tag_ids?: number[]; dynamic_tag_ids?: number[]; is_active?: boolean },
) {
  return useQuery({
    queryKey: ['line-user-profiles', params],
    queryFn: () => api.get<PaginatedResponse<LineUserProfileResponse>>('/api/integration/line/users', { params }),
    enabled: !!params,
  })
}

export function useAgentAutofillResults(
  profileId: number | undefined,
  params: AgentAutofillResultsRequest,
  enabled = true,
) {
  return useQuery({
    queryKey: ['agent-autofill-results', profileId, params.category],
    queryFn: () =>
      api.get<AgentAutofillResult[]>(`/api/integration/line/users/${profileId}/agent-autofill-results`, {
        params: { ...params },
      }),
    enabled: enabled && !!profileId,
    refetchOnMount: 'always',
  })
}

export function useExportAgentAutofillResults() {
  return useMutation({
    mutationFn: ({ profileId, ...params }: { profileId: number } & AgentAutofillResultsRequest) =>
      api.getBlob(`/api/integration/line/users/${profileId}/agent-autofill-results/export`, { params }),
  })
}

export function useLineUserProfile(id: number | undefined) {
  return useQuery({
    queryKey: ['line-user-profile', id],
    queryFn: () => api.get<LineUserProfileResponse>(`/api/integration/line/users/${id}`),
    enabled: !!id,
  })
}

export function usePatchLineUserProfileTags(profileId: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tagIds: number[]) =>
      api.patch<LineUserProfileResponse>(`/api/integration/line/users/${profileId}/tags`, { tag_ids: tagIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['line-user-profile', profileId] }),
  })
}

export function useSetLineUserProfileActive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ profileId, isActive }: { profileId: number; isActive: boolean }) =>
      api.post<LineUserProfileResponse>(
        `/api/integration/line/users/${profileId}/${isActive ? 'activate' : 'deactivate'}`,
        {},
      ),
    onSuccess: async (_, { profileId }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['line-user-profiles'] }),
        qc.invalidateQueries({ queryKey: ['line-user-profile', profileId] }),
      ])
    },
  })
}

// ============================================
// Company Model Hooks
// ============================================
export function useCompanyModels() {
  return useQuery({
    queryKey: ['company-models'],
    queryFn: () => api.get<{ result: AIModelProviderResponse[] }>('/api/company/models'),
    select: (data) => filterEnabledModels(data.result),
    staleTime: 5 * 60 * 1000,
  })
}

// ============================================
// Room Hooks
// ============================================
export function useRooms(params?: PaginationParams & { view?: string }) {
  return useQuery({
    queryKey: ['rooms', params],
    queryFn: () => api.get<PaginatedResponse<RoomResponse>>('/api/room/', { params }),
    enabled: !!params,
  })
}

export function useRoom(id: string | undefined) {
  return useQuery({
    queryKey: ['rooms', id],
    queryFn: () => api.get<RoomResponse>(`/api/room/${id}`),
    enabled: !!id,
  })
}

export function useCreateRoom(view = 'admin') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: RoomCreateRequest) =>
      api.post<RoomResponse>('/api/room/', toRoomFormData(data), { params: { view } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms'] }),
  })
}

export function useUpdateRoom() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RoomUpdateRequest }) =>
      api.patch<RoomResponse>(`/api/room/${id}`, toRoomFormData(data)),
    onSuccess: (room, { id }) => {
      qc.setQueryData(['rooms', id], room)
      return qc.invalidateQueries({ queryKey: ['rooms'] })
    },
  })
}

export function useDeleteRoom() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/room/${id}`),
    onSuccess: (_, id) => qc.invalidateQueries({ queryKey: ['rooms'], predicate: (query) => query.queryKey[1] !== id }),
  })
}

// ============================================
// Agent Hooks
// ============================================
export function useAgents(params?: PaginationParams) {
  return useQuery({
    queryKey: ['agents', params],
    queryFn: () =>
      api.get<PaginatedResponse<Agent>>('/api/agents', {
        params,
      }),
    enabled: !!params,
  })
}

export function useAgent(id: number | undefined) {
  return useQuery({
    queryKey: ['agents', id],
    queryFn: () => api.get<Agent>(`/api/agents/${id}`),
    enabled: !!id,
  })
}

export function useCreateAgent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: AgentCreateRequest) => api.post<Agent>('/api/agents', toAgentFormData(data)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  })
}

export function useUpdateAgent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgentUpdateRequest }) =>
      api.patch<Agent>(`/api/agents/${id}`, toAgentFormData(data)),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['agents'] })
      qc.invalidateQueries({ queryKey: ['agents', variables.id] })
    },
  })
}

export function useDeleteAgent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/agents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  })
}

export function useGetShareRoomDetail(roomId: string | undefined) {
  return useQuery({
    queryKey: ['rooms', 'share', roomId],
    queryFn: () => api.get<ShareRoomDetailResponse>(`/api/room/share/${roomId}`),
    enabled: !!roomId,
  })
}

export function useShareRoom() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ShareRoomRequest) => api.post<MessageResponse>('/api/room/share', data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['rooms', 'share', variables.uuid] })
      qc.invalidateQueries({ queryKey: ['rooms'] })
    },
  })
}

// ============================================
// Action Log Hooks
// ============================================
export function useAuditLogs(params?: PaginationParams & { actions?: string }) {
  return useQuery({
    queryKey: ['audit-logs', params],
    queryFn: () =>
      api.get<PaginatedResponse<AuditLogResponse>>('/api/logs/action-logs', {
        params,
      }),
    enabled: !!params,
  })
}

export function useDownloadBroadcastAudience() {
  return useMutation({
    mutationFn: (auditLogId: number) => api.getBlob(`/api/logs/action-logs/${auditLogId}/export/broadcast-audience`),
  })
}

// ============================================
// Static Tag Hooks
// ============================================
type StaticTagsParams = PaginationParams & { category?: string | string[]; is_enabled?: boolean }

export function useStaticTags(params?: StaticTagsParams) {
  const normalizedParams = params
    ? {
        ...params,
        category: Array.isArray(params.category)
          ? params.category.length > 0
            ? params.category.join(',')
            : undefined
          : params.category,
      }
    : undefined

  return useQuery({
    queryKey: ['static-tags', normalizedParams],
    queryFn: () =>
      api.get<PaginatedResponse<StaticTagResponse>>('/api/static-tags', {
        params: normalizedParams,
      }),
    enabled: !!params,
  })
}

export function useTestBroadcastAudienceCount() {
  const { data } = useStaticTags({
    category: 'system',
    search: 'test-group',
    page: 1,
    page_size: 1,
  })

  return data?.items[0]?.user_count ?? 0
}

export function useStaticTagCategories() {
  return useQuery({
    queryKey: ['static-tag-categories'],
    queryFn: () => api.get<{ categories: string[] }>('/api/static-tags/categories'),
  })
}

export function useStaticTag(id: number | undefined) {
  return useQuery({
    queryKey: ['static-tags', id],
    queryFn: () => api.get<StaticTagResponse>(`/api/static-tags/${id}`),
    enabled: !!id,
  })
}

export function useStaticTagImpact(id: number | undefined) {
  return useQuery({
    queryKey: ['static-tags', id, 'impact'],
    queryFn: () => api.get<TagImpactResponse>(`/api/static-tags/${id}/impact`),
    enabled: id !== undefined,
  })
}

function invalidateStaticTagAdminQueries(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['static-tags'] }),
    qc.invalidateQueries({ queryKey: ['static-tag-categories'] }),
    qc.invalidateQueries({ queryKey: ['tag-groups'] }),
    qc.invalidateQueries({ queryKey: ['tag-rules'] }),
  ])
}

export function useCreateStaticTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: StaticTagCreateRequest) => api.post<StaticTagResponse>('/api/static-tags', data),
    onSuccess: () => invalidateStaticTagAdminQueries(qc),
  })
}

export function useUpdateStaticTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: StaticTagUpdateRequest }) =>
      api.patch<StaticTagResponse>(`/api/static-tags/${id}`, data),
    onSuccess: () => invalidateStaticTagAdminQueries(qc),
  })
}

export function useDeleteStaticTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/static-tags/${id}`),
    onSuccess: () => invalidateStaticTagAdminQueries(qc),
  })
}

export function useMergeStaticTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: StaticTagMergeRequest) => api.post('/api/static-tags/merge', data),
    onSuccess: () => invalidateStaticTagAdminQueries(qc),
  })
}

// ============================================
// Dynamic Tag Hooks
// ============================================
export function useDynamicTagCategories(params?: Pick<PaginationParams, 'search' | 'ordering'>) {
  return useQuery({
    queryKey: ['dynamic-tag-categories', params],
    queryFn: () =>
      api.get<DynamicTagCategoryResponse[]>('/api/dynamic-tags/categories', {
        params,
      }),
    enabled: !!params,
  })
}

export function useCreateDynamicTagCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: DynamicTagCategoryCreateRequest) =>
      api.post<DynamicTagCategoryResponse>('/api/dynamic-tags/categories', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dynamic-tag-categories'] }),
  })
}

export function useUpdateDynamicTagCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DynamicTagCategoryUpdateRequest }) =>
      api.patch<DynamicTagCategoryResponse>(`/api/dynamic-tags/categories/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dynamic-tag-categories'] }),
  })
}

export function useDeleteDynamicTagCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/dynamic-tags/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dynamic-tag-categories'] }),
  })
}

// ============================================
// Session Category Hooks
// ============================================
export function useSessionCategories(params?: PaginationParams) {
  return useQuery({
    queryKey: ['session-categories', params],
    queryFn: () =>
      api.get<PaginatedResponse<SessionCategoryResponse>>('/api/chat/session-categories', {
        params,
      }),
    enabled: !!params,
  })
}

export function useSessionCategory(id: number | undefined) {
  return useQuery({
    queryKey: ['session-categories', id],
    queryFn: () => api.get<SessionCategoryResponse>(`/api/chat/session-categories/${id}`),
    enabled: !!id,
  })
}

export function useCreateSessionCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: SessionCategoryCreateRequest) =>
      api.post<SessionCategoryResponse>('/api/chat/session-categories', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session-categories'] }),
  })
}

export function useUpdateSessionCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SessionCategoryUpdateRequest }) =>
      api.patch<SessionCategoryResponse>(`/api/chat/session-categories/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session-categories'] }),
  })
}

export function useDeleteSessionCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/chat/session-categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session-categories'] }),
  })
}

export function useDynamicTags(params?: PaginationParams & { is_enabled?: boolean; category?: string }) {
  return useQuery({
    queryKey: ['dynamic-tags', params],
    queryFn: () =>
      api.get<PaginatedResponse<DynamicTagResponse>>('/api/dynamic-tags', {
        params,
      }),
    enabled: !!params,
  })
}

export function useDynamicTag(id: number | undefined) {
  return useQuery({
    queryKey: ['dynamic-tags', id],
    queryFn: () => api.get<DynamicTagResponse>(`/api/dynamic-tags/${id}`),
    enabled: !!id,
  })
}

export function useDynamicTagImpact(id: number | undefined) {
  return useQuery({
    queryKey: ['dynamic-tags', id, 'impact'],
    queryFn: () => api.get<TagImpactResponse>(`/api/dynamic-tags/${id}/impact`),
    enabled: id !== undefined,
  })
}

export function useUpdateDynamicTag(tagId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { auto_unassign_days: number | null }) =>
      api.patch<DynamicTagResponse>(`/api/dynamic-tags/${tagId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dynamic-tags'] }),
  })
}

export function useDeleteDynamicTag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tagId: number) => api.delete(`/api/dynamic-tags/${tagId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dynamic-tags'] }),
  })
}

export function useToggleDynamicTagEnabled() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ tagId, is_enabled }: { tagId: number; is_enabled: boolean }) =>
      api.patch<DynamicTagResponse>(`/api/dynamic-tags/${tagId}`, { is_enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dynamic-tags'] }),
  })
}

// ============================================
// Tag Group Hooks
// ============================================
export function useStaticTagGroups(params?: PaginationParams & { is_enabled?: string }) {
  return useQuery({
    queryKey: ['tag-groups', params],
    queryFn: () =>
      api.get<PaginatedResponse<StaticTagGroupResponse>>('/api/static-tag-groups', {
        params,
      }),
    enabled: !!params,
  })
}

export function useStaticTagGroup(id: number | undefined) {
  return useQuery({
    queryKey: ['tag-groups', id],
    queryFn: () => api.get<StaticTagGroupResponse>(`/api/static-tag-groups/${id}`),
    enabled: !!id,
  })
}

export function useCreateStaticTagGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; is_enabled?: boolean; tag_ids?: number[] }) =>
      api.post<StaticTagGroupResponse>('/api/static-tag-groups', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-groups'] }),
  })
}

export function useUpdateStaticTagGroup(groupId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name?: string; is_enabled?: boolean; tag_ids?: number[] }) =>
      api.patch<StaticTagGroupResponse>(`/api/static-tag-groups/${groupId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-groups'] }),
  })
}

export function useDeleteStaticTagGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (groupId: number) => api.delete(`/api/static-tag-groups/${groupId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-groups'] }),
  })
}

// ============================================
// Static Tag Rule Hooks
// ============================================
export function useTagRuleUsedTags() {
  return useQuery({
    queryKey: ['tag-rules', 'used-tags'],
    queryFn: () => api.get<Pick<StaticTagResponse, 'id' | 'name'>[]>('/api/static-tag-rules/used-tags'),
  })
}

export function useTagRules(
  params?: PaginationParams & {
    is_deleted?: boolean
    status?: string
    is_active?: boolean
    tag_ids?: string
    include_untagged?: boolean
  },
) {
  return useQuery({
    queryKey: ['tag-rules', params],
    queryFn: () =>
      api.get<PaginatedResponse<StaticTagRuleResponse>>('/api/static-tag-rules', {
        params,
      }),
    enabled: !!params,
  })
}

export function useTagRule(id: number | undefined) {
  return useQuery({
    queryKey: ['tag-rules', id],
    queryFn: () => api.get<StaticTagRuleResponse>(`/api/static-tag-rules/${id}`),
    enabled: !!id,
  })
}

export function useCreateStaticTagRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: StaticTagRuleCreateRequest) => api.post<StaticTagRuleResponse>('/api/static-tag-rules', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-rules'] }),
  })
}

export function useUpdateStaticTagRule(ruleId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: StaticTagRuleUpdateRequest) =>
      api.patch<StaticTagRuleResponse>(`/api/static-tag-rules/${ruleId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-rules'] }),
  })
}

export function useTriggerStaticTagRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: number) =>
      api.post<{ detail: string; assigned_count: number }>(`/api/static-tag-rules/${ruleId}/trigger`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-rules'] }),
  })
}

export function useDeleteStaticTagRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: number) => api.delete(`/api/static-tag-rules/${ruleId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-rules'] }),
  })
}

export function useActivateStaticTagRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: number) => api.post<StaticTagRuleResponse>(`/api/static-tag-rules/${ruleId}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-rules'] }),
  })
}

export function useDeactivateStaticTagRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: number) => api.post<StaticTagRuleResponse>(`/api/static-tag-rules/${ruleId}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tag-rules'] }),
  })
}

// ============================================
// Broadcast Template and Segment Hooks
// ============================================
export function useBroadcastCampaigns(params: BroadcastCampaignListParams) {
  return useQuery({
    queryKey: ['broadcast-campaigns', params],
    queryFn: () => api.get<PaginatedResponse<BroadcastCampaignResponse>>('/api/broadcast/campaigns', { params }),
  })
}

export function useCreateBroadcastCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BroadcastCampaignCreateRequest) =>
      api.post<BroadcastCampaignResponse>('/api/broadcast/campaigns', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] }),
  })
}

export function useBroadcastCampaign(id: number | undefined) {
  return useQuery({
    queryKey: ['broadcast-campaigns', id],
    queryFn: () => api.get<BroadcastCampaignDetailResponse>(`/api/broadcast/campaigns/${id}`),
    enabled: typeof id === 'number' && Number.isFinite(id),
  })
}

export function useBroadcastCampaignEditHistory(id: number | undefined, params: PaginationParams, enabled: boolean) {
  return useQuery({
    queryKey: ['broadcast-campaigns', id, 'edit-history', params],
    queryFn: () =>
      api.get<PaginatedResponse<AuditLogResponse>>(`/api/broadcast/campaigns/${id}/edit-history`, { params }),
    enabled: enabled && typeof id === 'number' && Number.isFinite(id),
  })
}

export function useBroadcastCampaignExecutionHistory(id: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['broadcast-campaigns', id, 'execution-history'],
    queryFn: () =>
      api.get<PaginatedResponse<BroadcastResultResponse>>(`/api/broadcast/campaigns/${id}/execution-history`, {
        params: { page: 1, page_size: 100 },
      }),
    enabled: enabled && typeof id === 'number' && Number.isFinite(id),
  })
}

export function useUpdateBroadcastCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: BroadcastCampaignUpdateRequest }) =>
      api.patch<BroadcastCampaignDetailResponse>(`/api/broadcast/campaigns/${id}`, data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.setQueryData(['broadcast-campaigns', data.id], data)
    },
  })
}

export function useLockBroadcastCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<BroadcastCampaignDetailResponse>(`/api/broadcast/campaigns/${id}/lock`),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.setQueryData(['broadcast-campaigns', data.id], data)
    },
  })
}

export function useDeleteBroadcastCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/broadcast/campaigns/${id}`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.removeQueries({ queryKey: ['broadcast-campaigns', id] })
    },
  })
}

export function useExecuteBroadcastCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<BroadcastResultResponse>(`/api/broadcast/campaigns/${id}/execute`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns', id] })
    },
    onError: (_, id) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns', id] })
    },
  })
}

export function useArchiveBroadcastCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<BroadcastCampaignDetailResponse>(`/api/broadcast/campaigns/${id}/archive`),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.setQueryData(['broadcast-campaigns', data.id], data)
    },
  })
}

export function useEnableBroadcastCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<BroadcastCampaignEnableResponse>(`/api/broadcast/campaigns/${id}/enable`),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.setQueryData(['broadcast-campaigns', data.campaign.id], data.campaign)
    },
  })
}

export function useCancelBroadcastCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<BroadcastCampaignDetailResponse>(`/api/broadcast/campaigns/${id}/cancel`),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.setQueryData(['broadcast-campaigns', data.id], data)
    },
  })
}

export function useSkipBroadcastCampaignRound() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<BroadcastCampaignDetailResponse>(`/api/broadcast/campaigns/${id}/skip-current-round`),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.setQueryData(['broadcast-campaigns', data.id], data)
    },
    onError: (_, id) => {
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] })
      qc.invalidateQueries({ queryKey: ['broadcast-campaigns', id] })
    },
  })
}

export function useBroadcastMessageTemplates(params?: PaginationParams) {
  return useQuery({
    queryKey: ['broadcast-message-templates', params],
    queryFn: () =>
      api.get<PaginatedResponse<BroadcastMessageTemplateResponse>>('/api/broadcast/message-templates', {
        params,
      }),
    enabled: !!params,
  })
}

export function useCreateBroadcastMessageTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BroadcastMessageTemplateCreateRequest) =>
      api.post<BroadcastMessageTemplateResponse>('/api/broadcast/message-templates', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-message-templates'] }),
  })
}

export function useTestBroadcastMessageTemplate() {
  return useMutation({
    mutationFn: (data: BroadcastMessageTemplateCreateRequest) =>
      api.post<BroadcastMessageTemplateTestBroadcastResponse>('/api/broadcast/message-templates/test-broadcast', data),
  })
}

export function useBroadcastMessageTemplate(id: number | undefined) {
  return useQuery({
    queryKey: ['broadcast-message-templates', id],
    queryFn: () => api.get<BroadcastMessageTemplateResponse>(`/api/broadcast/message-templates/${id}`),
    enabled: typeof id === 'number' && Number.isFinite(id),
  })
}

export function useUpdateBroadcastMessageTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: BroadcastMessageTemplateUpdateRequest }) =>
      api.patch<BroadcastMessageTemplateResponse>(`/api/broadcast/message-templates/${id}`, data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['broadcast-message-templates'] })
      qc.setQueryData(['broadcast-message-templates', data.id], data)
    },
  })
}

export function useDuplicateBroadcastMessageTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<BroadcastMessageTemplateResponse>(`/api/broadcast/message-templates/${id}/duplicate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-message-templates'] }),
  })
}

export function useDeleteBroadcastMessageTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<{ id: number }>(`/api/broadcast/message-templates/${id}`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['broadcast-message-templates'] })
      qc.removeQueries({ queryKey: ['broadcast-message-templates', id] })
    },
  })
}

export function useBroadcastSegments(params?: PaginationParams) {
  return useQuery({
    queryKey: ['broadcast-segments', params],
    queryFn: () =>
      api.get<PaginatedResponse<BroadcastSegmentResponse>>('/api/broadcast/segments', {
        params,
      }),
    enabled: !!params,
  })
}

export function useBroadcastSegment(id: number | undefined) {
  return useQuery({
    queryKey: ['broadcast-segments', id],
    queryFn: () => api.get<BroadcastSegmentDetailResponse>(`/api/broadcast/segments/${id}`),
    enabled: !!id,
  })
}

export function useBroadcastSegmentImpact(id: number | undefined) {
  return useQuery({
    queryKey: ['broadcast-segments', id, 'impact'],
    queryFn: () => api.get<BroadcastSegmentImpactResponse>(`/api/broadcast/segments/${id}/impact`),
    enabled: id !== undefined,
  })
}

export function useCreateBroadcastSegment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BroadcastSegmentCreateRequest) =>
      api.post<BroadcastSegmentResponse>('/api/broadcast/segments', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcast-segments'] }),
  })
}

export function useUpdateBroadcastSegment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: BroadcastSegmentUpdateRequest }) =>
      api.patch<BroadcastSegmentDetailResponse>(`/api/broadcast/segments/${id}`, data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['broadcast-segments'] })
      qc.setQueryData(['broadcast-segments', data.id], data)
    },
  })
}

export function useDeleteBroadcastSegment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<{ id: number }>(`/api/broadcast/segments/${id}`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['broadcast-segments'] })
      qc.removeQueries({ queryKey: ['broadcast-segments', id] })
    },
  })
}

export function useBroadcastSegmentPreview(data?: BroadcastSegmentPreviewRequest, enabled = true) {
  return useQuery({
    queryKey: ['broadcast-segment-preview', data],
    queryFn: () => api.post<BroadcastSegmentPreviewResponse>('/api/broadcast/segment-preview', data),
    enabled: !!data && enabled,
    placeholderData: (previousData) => previousData,
  })
}
